import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { useSWRConfig } from 'swr';
import { useRouter } from 'next/router';
import { CardMedia, Stack } from '@mui/material';
import { setJwtToken, setPortalKeyToken } from '@/utils/tokens';
import LoadingLottie from '@/components/LoadingLottie';
import { RevalidateApi, revalidateApiList } from '@/constant/apiList';
import PcBottomSheet, {
  PcBottomSheetProps,
} from '@/components/@IntranetLibrary/PcBottomSheet';

type BottomSheetConfig = Pick<
  PcBottomSheetProps,
  'title' | 'isExpandable' | 'additionalButton'
>;

type IframeData = {
  isRn: boolean;
  originalUrl: string;
  processedUrl: string; // 토큰 처리가 완료된 URL
  iframeFlag?: string;
  revalidateTarget?: RevalidateApi;
};

type OpenPayload = {
  isRn: boolean;
  url: string;
  iframeId?: string;
  revalidateTarget?: RevalidateApi;
} & BottomSheetConfig;

export type BottomSheetIframeContextProps = {
  openBottomSheet: (payload: OpenPayload) => void;
  closeBottomSheet: () => void;
};

const BottomSheetIframeContext =
  createContext<BottomSheetIframeContextProps | null>(null);
BottomSheetIframeContext.displayName = 'BottomSheetIframeContext';

const BottomSheetIframeProvider = ({ children }: PropsWithChildren) => {
  const { mutate } = useSWRConfig();
  const router = useRouter();

  const [isLoad, setIsLoad] = useState(false);
  const [iframeData, setIframeData] = useState<IframeData | null>(null);
  const [bottomSheetConfig, setBottomSheetConfig] = useState<BottomSheetConfig>(
    { title: '', isExpandable: true },
  );

  const isOpen = useMemo(() => {
    if (!iframeData) return false;
    // PC 환경에서 hash 체크 로직 (CSR 전용)
    if (!iframeData.isRn && iframeData.iframeFlag) {
      if (typeof window !== 'undefined') {
        // 현재 해시와 플래그 비교
        const currentHash = decodeURIComponent(window.location.hash).replace(
          /[/s|\\/]/g,
          '_',
        );
        return currentHash === `#${iframeData.iframeFlag}`;
      }
      return false;
    }
    return true;
  }, [iframeData, router.asPath]); // router.asPath 변경 감지

  /**
   * BottomSheet 열기
   */
  const openBottomSheet = useCallback(
    (payload: OpenPayload) => {
      const { isRn, url, iframeId, revalidateTarget, ...config } = payload;

      // 비즈니스 로직(특정 URL 경고)은 여기서 처리하기보다 호출부에서 처리하는 것이 좋으나,
      // 어디서 호출되는지 명확하지 않아 남겨둠
      if (url.includes('pop.fnf.co.kr/sales')) {
        alert('Sales 데이터는 2024년 5월 1일부터 밀키웨이에서 제공됩니다.');
      }

      if (isRn) {
        // RN Webview 통신 로직
        if (url.includes('/organization')) {
          if (window.isRNWebview && window.ReactNativeWebView) {
            const sendData = JSON.stringify({ actionType: 'MODAL', url });
            return window.ReactNativeWebView.postMessage(sendData);
          } else {
            return window.open(url, '_blank', 'noopener, noreferrer');
          }
        }

        // RN: 토큰 처리 미리 수행
        setIframeData({
          isRn,
          originalUrl: url,
          processedUrl: setPortalKeyToken(url), // 렌더링 전 미리 계산
        });
      } else {
        // PC Web
        const flag = iframeId
          ? `${iframeId.replaceAll('/', '_')}_open`
          : undefined;

        setIframeData({
          isRn,
          originalUrl: url,
          processedUrl: setJwtToken(url), // 렌더링 전 미리 계산
          iframeFlag: flag,
          revalidateTarget,
        });

        if (flag) {
          router.push(
            {
              pathname: router.pathname,
              query: router.query,
              hash: flag,
            },
            undefined,
            { shallow: true },
          );
        }
      }

      setBottomSheetConfig(config);
      setIsLoad(false);
    },
    [router],
  );

  /** BottomSheet 닫기 */
  const closeBottomSheet = useCallback(() => {
    if (!iframeData) return;

    if (iframeData.revalidateTarget) {
      const targets = revalidateApiList.get(iframeData.revalidateTarget);
      targets?.forEach((apiUrl) =>
        mutate(apiUrl, undefined, { revalidate: true }),
      );
    }

    // PC Web일 경우 URL 정리
    if (!iframeData.isRn) {
      router.replace(
        {
          pathname: router.pathname,
          query: router.query,
        },
        undefined,
        { shallow: true },
      );
    }

    setIframeData(null);
    setIsLoad(false);
  }, [iframeData, mutate, router]);

  // iframe 로드 핸들러
  const handleIframeLoad = useCallback(() => {
    setIsLoad(true);
  }, []);

  return (
    <BottomSheetIframeContext.Provider
      value={{ openBottomSheet, closeBottomSheet }}
    >
      {children}
      <PcBottomSheet
        isOpen={isOpen}
        handleClose={closeBottomSheet}
        {...bottomSheetConfig}
      >
        {iframeData && (
          <>
            {iframeData.isRn && !isLoad && (
              <Stack
                direction={'column'}
                justifyContent={'center'}
                sx={{ position: 'relative', width: '100%' }}
              >
                <LoadingLottie opacity={1} loop={true} />
              </Stack>
            )}
            <CardMedia
              id="full-page-iframe"
              component="iframe"
              sx={{
                height: '100%',
                border: 'none',
                display: iframeData.isRn && !isLoad ? 'none' : 'block', // 로딩 중 깜빡임 방지 처리
              }}
              onLoad={handleIframeLoad}
              src={iframeData.processedUrl}
            />
          </>
        )}
      </PcBottomSheet>
    </BottomSheetIframeContext.Provider>
  );
};

export { BottomSheetIframeProvider, BottomSheetIframeContext };
